import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';
import { isCountryCode } from '@/shared/lib/countries';

import { M, zOptionalMoney } from '@/business';
import { isValidUaeIban } from '@/shared/lib/identifiers';

/**
 * Bank application parts of the deal form (A2): customer e-mails, household profile, addresses, liabilities,
 * references, finance additions. Values the server returns masked (contain "•") are valid as they are and are never
 * sent back; an emptied field clears the value on file.
 */
export const MASKED = /•/;
const isMasked = (v: unknown): boolean => typeof v === "string" && MASKED.test(v);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9 ()-]{7,24}$/;

export const EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'] as const satisfies readonly Schema<'Emirate'>[];
export const ADDRESS_KINDS = ['residential', 'home_country', 'office'] as const satisfies readonly Schema<'CustomerAddressKind'>[];
export const MARITAL = ['married', 'single'] as const satisfies readonly Schema<'MaritalStatus'>[];
export const PROFILE_TYPES = ['salaried', 'self_employed'] as const satisfies readonly Schema<'ProfileType'>[];
export const ACCOMMODATION = ['rented', 'sharing', 'owned', 'company_sponsored'] as const satisfies readonly Schema<'AccommodationType'>[];
export const PROFILE_COUNTS = ['dependants', 'childrenInUae', 'childrenAtSchoolInUae', 'propertiesOwnedInUae', 'domesticWorkers'] as const;
/** Profile fields grouped as laid out on the form. */
export const PROFILE_FAMILY_COUNTS = ['dependants', 'childrenInUae', 'childrenAtSchoolInUae'] as const;
export const PROFILE_OTHER_COUNTS = ['propertiesOwnedInUae', 'domesticWorkers'] as const;
export const PROFILE_MONEY = ['domesticWorkersWages', 'otherObligationsMonthly', 'otherGuaranteesValue', 'supportPaymentsMonthly'] as const;
export type ProfileKey = Schema<'ProfileFieldKey'>;
export type AddressKind = (typeof ADDRESS_KINDS)[number];
/** Address parts per kind, in the order of the bank's form. */
export const ADDRESS_PARTS: Record<AddressKind, readonly AddressPart[]> = {
  residential: ['flat', 'building', 'area', 'street', 'landmark', 'emirate'],
  home_country: ['flat', 'building', 'area', 'street', 'city', 'country', 'contactNumber'],
  office: ['officeName', 'area', 'street', 'landmark', 'emirate', 'poBox', 'landline'],
};
export type AddressPart = Schema<'CustomerAddressPart'>;
/** Every address part in the order the parts are laid out (standard parts of each kind keep their order). */
export const ALL_ADDRESS_PARTS: readonly AddressPart[] = ['officeName', 'flat', 'building', 'area', 'street', 'landmark', 'city', 'emirate', 'country', 'poBox', 'landline', 'contactNumber'];
/**
 * C3 · the parts shown for an address kind: the standard ones plus the bank's additional parts from its configuration
 * (e.g. SIB: residential P.O. Box and home telephone, home-country landmark and P.O. Box, office building name).
 */
export function addressPartsFor(kind: AddressKind, requirements: Pick<Schema<'ApplicationRequirements'>, 'additionalAddressParts'> | null | undefined): readonly AddressPart[] {
  const extra = requirements?.additionalAddressParts?.[kind] ?? [];
  if (!extra.length) return ADDRESS_PARTS[kind];
  const wanted = new Set<AddressPart>([...ADDRESS_PARTS[kind], ...extra]);
  return ALL_ADDRESS_PARTS.filter((p) => wanted.has(p));
}

const text = (max: number) => z.string().max(max, 'form.tooLong').nullable().optional();
const email = () => z.string().nullable().optional().refine((v) => !v || isMasked(v) || EMAIL.test(v.trim()), M.email);
const phone = () => z.string().nullable().optional().refine((v) => !v || isMasked(v) || PHONE.test(v.trim()), 'validation.server.format');
const count = (max: number) => z.number().int().min(0, 'validation.server.range').max(max, 'validation.server.range').nullable().optional();
const iban = () => z.string().nullable().optional().refine((v) => !v || isMasked(v) || isValidUaeIban(v), M.iban);
const triState = z.enum(['yes', 'no', 'unknown']);

export const profileSchema = z.object({
  familyInUae: triState,
  maritalStatus: z.enum(MARITAL).nullable().optional(),
  profileType: z.enum(PROFILE_TYPES).nullable().optional(),
  dependants: count(99),
  childrenInUae: count(99),
  childrenAtSchoolInUae: count(99),
  accommodation: z.enum(ACCOMMODATION).nullable().optional(),
  propertiesOwnedInUae: count(999),
  domesticWorkers: count(99),
  domesticWorkersWages: zOptionalMoney(),
  otherObligationsMonthly: zOptionalMoney(),
  otherGuaranteesValue: zOptionalMoney(),
  supportPaymentsMonthly: zOptionalMoney(),
});

const addressSchema = z.object({
  emirate: z.enum(EMIRATES).nullable().optional(),
  area: text(120),
  city: text(120),
  country: z.string().nullable().optional().refine((v) => !v || isCountryCode(v.trim().toUpperCase()), 'validation.server.invalid_country'),
  officeName: text(160),
  flat: text(40),
  building: text(160),
  street: text(160),
  landmark: text(160),
  poBox: text(20),
  landline: phone(),
  contactNumber: phone(),
});

export const liabilityRow = z.object({
  id: z.string().optional(),
  bankId: z.string({ error: M.required }).min(1, M.required),
  facilityType: z.enum(['personal_finance', 'credit_card', 'auto_finance', 'home_finance', 'overdraft', 'other']),
  outstandingAmount: zOptionalMoney(),
  monthlyInstalment: zOptionalMoney(),
  buyout: z.boolean(),
  iban: iban(),
  note: text(500),
});

export const referenceRow = z
  .object({
    id: z.string().optional(),
    name: text(160),
    phone: z.string().nullable().optional().refine((v) => !v || isMasked(v) || /^\+9715\d{8}$/.test(v.replace(/\s/g, '')) || /^\d{7,15}$/.test(v.replace(/[\s()+-]/g, '')), 'validation.server.format'),
    relationship: text(80),
    note: text(500),
  })
  .refine((r) => Boolean(r.id) || Boolean(r.name?.trim()), { message: M.required, path: ['name'] });

/** Schema parts merged into the deal form schema. */
export const applicationShape = {
  email: email(),
  officialEmail: email(),
  salaryTransfer: triState,
  retiringBankCode: z.string().nullable().optional(),
  retiringBankIban: iban(),
  profile: profileSchema,
  addresses: z.object({ residential: addressSchema, home_country: addressSchema, office: addressSchema }),
  liabilities: z.array(liabilityRow),
  references: z.array(referenceRow).max(5, 'validation.server.max'),
};

export const hrShape = { hrContactName: text(160), hrPhone: phone(), hrEmail: email() };

export type ProfileValues = z.infer<typeof profileSchema>;
export type AddressValues = z.infer<typeof addressSchema>;
export type LiabilityValues = z.infer<typeof liabilityRow>;
export type ReferenceValues = z.infer<typeof referenceRow>;
export interface ApplicationValues {
  email?: string | null | undefined;
  officialEmail?: string | null | undefined;
  salaryTransfer: 'yes' | 'no' | 'unknown';
  retiringBankCode?: string | null | undefined;
  retiringBankIban?: string | null | undefined;
  profile: ProfileValues;
  addresses: Record<AddressKind, AddressValues>;
  liabilities: LiabilityValues[];
  references: ReferenceValues[];
}

const tri = (v: boolean | null | undefined): 'yes' | 'no' | 'unknown' => (v === true ? 'yes' : v === false ? 'no' : 'unknown');
const fromTri = (v: 'yes' | 'no' | 'unknown') => (v === 'yes' ? true : v === 'no' ? false : null);

// ------------------------------------------------------------------ dto → form values

export function toApplicationValues(v: Schema<'DealFormValues'>): ApplicationValues {
  const p = (v.profile ?? {});
  const byKind = new Map((v.addresses ?? []).map((a) => [a.kind, a]));
  const address = (kind: AddressKind): AddressValues => {
    const a = byKind.get(kind);
    return { emirate: a?.emirate ?? null, area: a?.area ?? null, city: a?.city ?? null, country: a?.country ?? null, officeName: a?.officeName ?? null, flat: a?.flat ?? null, building: a?.building ?? null, street: a?.street ?? null, landmark: a?.landmark ?? null, poBox: a?.poBox ?? null, landline: a?.landline ?? null, contactNumber: a?.contactNumber ?? null };
  };
  return {
    email: v.email ?? null,
    officialEmail: v.officialEmail ?? null,
    salaryTransfer: tri(v.salaryTransfer),
    retiringBankCode: v.retiringBankCode ?? null,
    retiringBankIban: v.retiringBankIban ?? null,
    profile: {
      familyInUae: tri(p.familyInUae),
      maritalStatus: p.maritalStatus ?? null,
      profileType: p.profileType ?? null,
      dependants: p.dependants ?? null,
      childrenInUae: p.childrenInUae ?? null,
      childrenAtSchoolInUae: p.childrenAtSchoolInUae ?? null,
      accommodation: p.accommodation ?? null,
      propertiesOwnedInUae: p.propertiesOwnedInUae ?? null,
      domesticWorkers: p.domesticWorkers ?? null,
      domesticWorkersWages: p.domesticWorkersWages ?? null,
      otherObligationsMonthly: p.otherObligationsMonthly ?? null,
      otherGuaranteesValue: p.otherGuaranteesValue ?? null,
      supportPaymentsMonthly: p.supportPaymentsMonthly ?? null,
    },
    addresses: { residential: address('residential'), home_country: address('home_country'), office: address('office') },
    liabilities: (v.liabilities ?? []).map((l) => ({ ...(l.id ? { id: l.id } : {}), bankId: l.bankId, facilityType: l.facilityType, outstandingAmount: l.outstandingAmount ?? null, monthlyInstalment: l.monthlyInstalment ?? null, buyout: l.buyout, iban: l.iban ?? null, note: l.note ?? null })),
    references: (v.references ?? []).map((r) => ({ ...(r.id ? { id: r.id } : {}), name: r.name ?? null, phone: r.phone ?? null, relationship: r.relationship ?? null, note: r.note ?? null })),
  };
}

// ------------------------------------------------------------------ form values → update (only dirty keys)

/** Text for the server: masked → left out (kept on file); empty → null (cleared). */
function out(v: string | null | undefined): string | null | undefined {
  if (v === undefined) return undefined;
  if (isMasked(v)) return undefined;
  const t = v?.trim() ?? '';
  return t ? t : null;
}
const compact = <T extends Record<string, unknown>>(o: T) => Object.fromEntries(Object.entries(o).filter(([, x]) => x !== undefined)) as Partial<T>;
type Dirty = Record<string, unknown> | boolean | undefined;
const dirtyKeys = (d: Dirty): string[] => (d && typeof d === 'object' ? Object.keys(d).filter((k) => d[k]) : []);

export function applicationUpdate(values: ApplicationValues, dirty: Record<string, Dirty>): Partial<Schema<'DealUpdate'>> {
  const body: Partial<Schema<'DealUpdate'>> = {};
  if (dirty['email']) {
    const e = out(values.email);
    if (e !== undefined) body.email = e;
  }
  if (dirty['officialEmail']) {
    const e = out(values.officialEmail);
    if (e !== undefined) body.officialEmail = e;
  }
  if (dirty['salaryTransfer']) body.salaryTransfer = fromTri(values.salaryTransfer);
  if (dirty['retiringBankCode']) body.retiringBankCode = values.retiringBankCode || null;
  if (dirty['retiringBankIban']) {
    const i = out(values.retiringBankIban);
    if (i !== undefined) body.retiringBankIban = i === null ? null : i.replace(/\s/g, '');
  }
  const profileKeys = dirtyKeys(dirty['profile']) as ProfileKey[];
  if (profileKeys.length) {
    const p: Record<string, unknown> = {};
    for (const k of profileKeys) p[k] = k === 'familyInUae' ? fromTri(values.profile.familyInUae) : (values.profile[k] ?? null);
    body.profile = p;
  }
  const addressDirty = dirty['addresses'] && typeof dirty['addresses'] === 'object' ? (dirty['addresses'] as Record<string, Dirty>) : {};
  const kinds = ADDRESS_KINDS.filter((k) => dirtyKeys(addressDirty[k]).length);
  if (kinds.length) {
    body.addresses = kinds.map((kind) => {
      const a = values.addresses[kind];
      const parts: Record<string, unknown> = {};
      for (const part of dirtyKeys(addressDirty[kind]) as AddressPart[]) {
        if (part === 'emirate') parts[part] = a.emirate ?? null;
        else if (part === 'country') parts[part] = a.country ? a.country.trim().toUpperCase() : null;
        else parts[part] = out(a[part]);
      }
      return { kind, ...compact(parts) };
    });
  }
  if (dirty['liabilities'])
    body.liabilities = values.liabilities.map((l) =>
      compact({ id: l.id, bankId: l.bankId, facilityType: l.facilityType, outstandingAmount: l.outstandingAmount ?? null, monthlyInstalment: l.monthlyInstalment ?? null, buyout: l.buyout, iban: out(l.iban)?.replace(/\s/g, '') ?? (out(l.iban) === null ? null : undefined), note: out(l.note) ?? null }),
    ) as Schema<'DealLiabilityInput'>[];
  if (dirty['references'])
    body.references = values.references.map((r) => compact({ id: r.id, name: out(r.name), phone: out(r.phone)?.replace(/\s/g, '') ?? (out(r.phone) === null ? null : undefined), relationship: out(r.relationship) ?? null, note: out(r.note) ?? null }));
  return body;
}

/** HR contact of one employment for the update (masked parts left out). */
export function hrUpdate(e: { hrContactName?: string | null | undefined; hrPhone?: string | null | undefined; hrEmail?: string | null | undefined }) {
  return compact({ hrContactName: out(e.hrContactName), hrPhone: out(e.hrPhone), hrEmail: out(e.hrEmail) });
}
